import Button from 'devextreme-react/button';
import Popup from 'devextreme-react/popup';
import SelectBox from 'devextreme-react/select-box';
import Switch from 'devextreme-react/switch';
import TextBox from 'devextreme-react/text-box';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { showConfirmDialog, showErrorToast, showSuccessToast } from '@jarvis/platform-ui';
import {
  clearMediaLibraryStats,
  createAdminCategory,
  createAdminTag,
  emptyMediaLibrary,
  fetchAdminCategories,
  fetchAdminTags,
  mergeAdminCategory,
  mergeAdminTag,
  resetMediaLibraryTaxonomy,
  updateAdminCategory,
  updateAdminTag,
  type MediaCategoryAdmin,
  type MediaTagAdmin
} from '../../../api/mediaLibrary';

const UNCATEGORIZED_ID = '11111111-1111-1111-1111-111111111111';

type MergeKind = 'category' | 'tag';

interface MergeState {
  kind: MergeKind;
  sourceId: string;
  sourceName: string;
  usageCount: number;
  targetId: string | null;
}

export function MediaLibraryMetadataPage() {
  const [categories, setCategories] = useState<MediaCategoryAdmin[]>([]);
  const [tags, setTags] = useState<MediaTagAdmin[]>([]);
  const [newCategory, setNewCategory] = useState('');
  const [newTag, setNewTag] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [editingTagName, setEditingTagName] = useState('');
  const [merge, setMerge] = useState<MergeState | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [cats, tagRows] = await Promise.all([fetchAdminCategories(), fetchAdminTags()]);
      setCategories(cats);
      setTags(tagRows);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not load media metadata.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const mergeTargets = useMemo(() => {
    if (!merge) return [];
    if (merge.kind === 'category') {
      return categories
        .filter((c) => c.id !== merge.sourceId && c.isActive)
        .map((c) => ({ id: c.id, name: c.name }));
    }
    return tags
      .filter((t) => t.id !== merge.sourceId && t.isActive)
      .map((t) => ({ id: t.id, name: t.name }));
  }, [categories, merge, tags]);

  async function onCreateCategory() {
    const name = newCategory.trim();
    if (!name) return;
    try {
      await createAdminCategory({ name });
      setNewCategory('');
      showSuccessToast('Category created.');
      await reload();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not create category.');
    }
  }

  async function onCreateTag() {
    const name = newTag.trim();
    if (!name) return;
    try {
      await createAdminTag(name);
      setNewTag('');
      showSuccessToast('Tag created.');
      await reload();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not create tag.');
    }
  }

  function beginRenameCategory(category: MediaCategoryAdmin) {
    setEditingCategoryId(category.id);
    setEditingCategoryName(category.name);
    setEditingTagId(null);
  }

  async function saveRenameCategory(category: MediaCategoryAdmin) {
    const name = editingCategoryName.trim();
    if (!name || name === category.name) {
      setEditingCategoryId(null);
      return;
    }
    try {
      await updateAdminCategory(category.id, { name });
      showSuccessToast('Category renamed.');
      setEditingCategoryId(null);
      await reload();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Rename failed.');
    }
  }

  async function moveCategory(index: number, direction: -1 | 1) {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= categories.length) return;
    const reordered = [...categories];
    [reordered[index], reordered[nextIndex]] = [reordered[nextIndex], reordered[index]];
    try {
      await Promise.all(
        reordered.map((category, position) =>
          updateAdminCategory(category.id, { sortOrder: (position + 1) * 10 })
        )
      );
      await reload();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not reorder categories.');
    }
  }

  function openMergeCategory(source: MediaCategoryAdmin) {
    if (source.id === UNCATEGORIZED_ID) {
      showErrorToast('Uncategorized cannot be merged into another category.');
      return;
    }
    const targets = categories.filter((c) => c.id !== source.id && c.isActive);
    if (targets.length === 0) {
      showErrorToast('No active target category available.');
      return;
    }
    setMerge({
      kind: 'category',
      sourceId: source.id,
      sourceName: source.name,
      usageCount: source.usageCount,
      targetId: targets[0]?.id ?? null
    });
  }

  function openMergeTag(source: MediaTagAdmin) {
    const targets = tags.filter((t) => t.id !== source.id && t.isActive);
    if (targets.length === 0) {
      showErrorToast('No active target tag available.');
      return;
    }
    setMerge({
      kind: 'tag',
      sourceId: source.id,
      sourceName: source.name,
      usageCount: source.usageCount,
      targetId: targets[0]?.id ?? null
    });
  }

  async function confirmMerge() {
    if (!merge?.targetId) {
      showErrorToast('Select a merge target.');
      return;
    }
    const target = mergeTargets.find((t) => t.id === merge.targetId);
    if (!target) {
      showErrorToast('Invalid merge target.');
      return;
    }

    const confirmed = await showConfirmDialog({
      title: merge.kind === 'category' ? 'Confirm category merge' : 'Confirm tag merge',
      message:
        merge.kind === 'category'
          ? `Merge “${merge.sourceName}” into “${target.name}”? ${merge.usageCount} asset(s) will be reassigned and “${merge.sourceName}” will be deactivated.`
          : `Merge “${merge.sourceName}” into “${target.name}”? Tag links will be reassigned and “${merge.sourceName}” will be deactivated.`,
      confirmText: 'Merge',
      danger: true
    });
    if (!confirmed) return;

    setBusy(true);
    try {
      if (merge.kind === 'category') await mergeAdminCategory(merge.sourceId, merge.targetId);
      else await mergeAdminTag(merge.sourceId, merge.targetId);
      showSuccessToast(`Merged into ${target.name}.`);
      setMerge(null);
      await reload();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Merge failed.');
    } finally {
      setBusy(false);
    }
  }

  function beginRenameTag(tag: MediaTagAdmin) {
    setEditingTagId(tag.id);
    setEditingTagName(tag.name);
    setEditingCategoryId(null);
  }

  async function saveRenameTag(tag: MediaTagAdmin) {
    const name = editingTagName.trim();
    if (!name || name === tag.name) {
      setEditingTagId(null);
      return;
    }
    try {
      await updateAdminTag(tag.id, { name });
      showSuccessToast('Tag renamed.');
      setEditingTagId(null);
      await reload();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Rename failed.');
    }
  }

  async function onClearStats() {
    const confirmed = await showConfirmDialog({
      title: 'Clear all stats',
      message:
        'Clear usage counters, ratings, reactions, and usage history for managed GIFs and memes? Assets and My Uploads are not deleted.',
      confirmText: 'Clear all stats',
      danger: true
    });
    if (!confirmed || busy) return;
    setBusy(true);
    try {
      const result = await clearMediaLibraryStats();
      showSuccessToast(
        `Cleared ${result.clearedStatsRows} stats row(s), ${result.deletedRatings} rating(s), ${result.deletedReactions} reaction(s), ${result.deletedUsageEvents} event(s).`
      );
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not clear stats.');
    } finally {
      setBusy(false);
    }
  }

  async function onEmptyLibrary(library: 'gifs' | 'memes') {
    const label = library === 'gifs' ? 'GIFs' : 'Memes';
    const confirmed = await showConfirmDialog({
      title: `Empty ${label}`,
      message: `Permanently delete all managed ${label} from the database and object storage? This cannot be undone. My Uploads are not affected.`,
      confirmText: `Empty ${label}`,
      danger: true
    });
    if (!confirmed || busy) return;
    setBusy(true);
    try {
      const result = await emptyMediaLibrary(library);
      showSuccessToast(
        `Removed ${result.deletedAssets} asset(s) and ${result.deletedObjects} object(s) from ${label}.`
      );
      await reload();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : `Could not empty ${label}.`);
    } finally {
      setBusy(false);
    }
  }

  async function onResetTaxonomy() {
    const confirmed = await showConfirmDialog({
      title: 'Reset taxonomy',
      message:
        'Restore default categories, delete all tags, and move managed assets on custom categories to Uncategorized? My Uploads are not affected.',
      confirmText: 'Reset taxonomy',
      danger: true
    });
    if (!confirmed || busy) return;
    setBusy(true);
    try {
      const result = await resetMediaLibraryTaxonomy();
      showSuccessToast(
        `Removed ${result.deletedCategories} categor${result.deletedCategories === 1 ? 'y' : 'ies'} and ${result.deletedTags} tag(s); restored ${result.restoredCategories} default categories.`
      );
      await reload();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not reset taxonomy.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ops-page ops-fill-page" style={{ padding: '1.25rem', overflow: 'auto' }}>
      <header
        style={{
          marginBottom: '1rem',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.75rem 1rem',
          alignItems: 'flex-start',
          justifyContent: 'space-between'
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: '1.35rem' }}>Media Library Config</h1>
          <p className="utility-app__muted" style={{ margin: '0.35rem 0 0' }}>
            Manage shared GIF/meme categories and tags. Prefer deactivate or merge over delete when
            items are in use. Library controls never touch My Uploads.
          </p>
        </div>
        <div
          role="toolbar"
          aria-label="Library controls"
          style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}
        >
          <Button
            text="Clear all stats"
            stylingMode="outlined"
            type="danger"
            disabled={busy}
            onClick={() => void onClearStats()}
          />
          <Button
            text="Empty GIFs"
            stylingMode="contained"
            type="danger"
            disabled={busy}
            onClick={() => void onEmptyLibrary('gifs')}
          />
          <Button
            text="Empty Memes"
            stylingMode="contained"
            type="danger"
            disabled={busy}
            onClick={() => void onEmptyLibrary('memes')}
          />
          <Button
            text="Reset taxonomy"
            stylingMode="contained"
            type="danger"
            disabled={busy}
            onClick={() => void onResetTaxonomy()}
          />
        </div>
      </header>

      {loading ? <p className="utility-app__muted">Loading…</p> : null}

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.05rem' }}>Categories</h2>
        <div style={{ display: 'flex', gap: '0.5rem', maxWidth: 480, marginBottom: '0.75rem' }}>
          <TextBox
            value={newCategory}
            width="100%"
            placeholder="New category name"
            onValueChanged={(e) => setNewCategory(String(e.value ?? ''))}
          />
          <Button text="Add" type="default" disabled={busy} onClick={() => void onCreateCategory()} />
        </div>
        <div style={{ display: 'grid', gap: '0.5rem' }}>
          {categories.map((category, index) => (
            <div
              key={category.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto auto auto',
                gap: '0.75rem',
                alignItems: 'center',
                border: '1px solid var(--ops-border, #d4d4d8)',
                borderRadius: 8,
                padding: '0.65rem 0.75rem'
              }}
            >
              <div>
                {editingCategoryId === category.id ? (
                  <TextBox
                    value={editingCategoryName}
                    width="100%"
                    onValueChanged={(e) => setEditingCategoryName(String(e.value ?? ''))}
                    onEnterKey={() => void saveRenameCategory(category)}
                  />
                ) : (
                  <strong>{category.name}</strong>
                )}
                <div className="utility-app__muted" style={{ fontSize: '0.8rem' }}>
                  {category.usageCount} assets · sort {category.sortOrder}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                {editingCategoryId === category.id ? (
                  <>
                    <Button text="Save" stylingMode="text" onClick={() => void saveRenameCategory(category)} />
                    <Button text="Cancel" stylingMode="text" onClick={() => setEditingCategoryId(null)} />
                  </>
                ) : (
                  <Button text="Rename" stylingMode="text" onClick={() => beginRenameCategory(category)} />
                )}
                <Button
                  text="Merge into…"
                  stylingMode="text"
                  disabled={category.id === UNCATEGORIZED_ID || busy}
                  onClick={() => openMergeCategory(category)}
                />
                <Button
                  icon="chevronup"
                  hint="Move up"
                  stylingMode="text"
                  disabled={index === 0 || busy}
                  onClick={() => void moveCategory(index, -1)}
                />
                <Button
                  icon="chevrondown"
                  hint="Move down"
                  stylingMode="text"
                  disabled={index === categories.length - 1 || busy}
                  onClick={() => void moveCategory(index, 1)}
                />
              </div>
              <span className="utility-app__muted" style={{ fontSize: '0.8rem' }}>
                Active
              </span>
              <Switch
                value={category.isActive}
                disabled={busy}
                onValueChanged={(e) => {
                  void updateAdminCategory(category.id, { isActive: Boolean(e.value) })
                    .then(() => reload())
                    .catch((err) =>
                      showErrorToast(err instanceof Error ? err.message : 'Update failed.')
                    );
                }}
              />
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 style={{ fontSize: '1.05rem' }}>Tags</h2>
        <div style={{ display: 'flex', gap: '0.5rem', maxWidth: 480, marginBottom: '0.75rem' }}>
          <TextBox
            value={newTag}
            width="100%"
            placeholder="New tag name"
            onValueChanged={(e) => setNewTag(String(e.value ?? ''))}
          />
          <Button text="Add" type="default" disabled={busy} onClick={() => void onCreateTag()} />
        </div>
        <div style={{ display: 'grid', gap: '0.5rem' }}>
          {tags.map((tag) => (
            <div
              key={tag.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto auto auto',
                gap: '0.75rem',
                alignItems: 'center',
                border: '1px solid var(--ops-border, #d4d4d8)',
                borderRadius: 8,
                padding: '0.65rem 0.75rem'
              }}
            >
              <div>
                {editingTagId === tag.id ? (
                  <TextBox
                    value={editingTagName}
                    width="100%"
                    onValueChanged={(e) => setEditingTagName(String(e.value ?? ''))}
                    onEnterKey={() => void saveRenameTag(tag)}
                  />
                ) : (
                  <strong>{tag.name}</strong>
                )}
                <div className="utility-app__muted" style={{ fontSize: '0.8rem' }}>
                  {tag.usageCount} assets
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                {editingTagId === tag.id ? (
                  <>
                    <Button text="Save" stylingMode="text" onClick={() => void saveRenameTag(tag)} />
                    <Button text="Cancel" stylingMode="text" onClick={() => setEditingTagId(null)} />
                  </>
                ) : (
                  <Button text="Rename" stylingMode="text" onClick={() => beginRenameTag(tag)} />
                )}
                <Button
                  text="Merge into…"
                  stylingMode="text"
                  disabled={busy}
                  onClick={() => openMergeTag(tag)}
                />
              </div>
              <span className="utility-app__muted" style={{ fontSize: '0.8rem' }}>
                Active
              </span>
              <Switch
                value={tag.isActive}
                disabled={busy}
                onValueChanged={(e) => {
                  void updateAdminTag(tag.id, { isActive: Boolean(e.value) })
                    .then(() => reload())
                    .catch((err) =>
                      showErrorToast(err instanceof Error ? err.message : 'Update failed.')
                    );
                }}
              />
            </div>
          ))}
        </div>
      </section>

      <Popup
        visible={merge != null}
        onHiding={() => !busy && setMerge(null)}
        title={merge?.kind === 'tag' ? 'Merge tag' : 'Merge category'}
        showCloseButton={!busy}
        hideOnOutsideClick={!busy}
        width={440}
        height="auto"
      >
        {merge ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', paddingTop: '0.25rem' }}>
            <p className="utility-app__muted" style={{ margin: 0 }}>
              Merge <strong>{merge.sourceName}</strong> into another active{' '}
              {merge.kind === 'category' ? 'category' : 'tag'}. The source will be deactivated.
            </p>
            <label className="utility-app__label" htmlFor="media-lib-merge-target">
              Target
            </label>
            <SelectBox
              inputAttr={{ id: 'media-lib-merge-target' }}
              dataSource={mergeTargets}
              displayExpr="name"
              valueExpr="id"
              value={merge.targetId}
              width="100%"
              disabled={busy}
              onValueChanged={(e) =>
                setMerge((prev) =>
                  prev ? { ...prev, targetId: (e.value as string | null) ?? null } : prev
                )
              }
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <Button text="Cancel" stylingMode="outlined" disabled={busy} onClick={() => setMerge(null)} />
              <Button
                text="Merge"
                type="danger"
                stylingMode="contained"
                disabled={busy || !merge.targetId}
                onClick={() => void confirmMerge()}
              />
            </div>
          </div>
        ) : null}
      </Popup>
    </div>
  );
}
